import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";

const PORTRAIT =
  "https://images.unsplash.com/photo-1605602517387-ec78b947335e?crop=entropy&cs=srgb&fm=jpg&q=85";

const LINES = ["SERVICE.", "INTEGRITY.", "BUNGOMA 2027."];

const STATS = [
  { n: "12+", l: "Years as Westlands MP" },
  { n: "3", l: "Terms won on the ODM ticket" },
  { n: "2004", l: "Co-founded Kenya Paraplegic Org." },
  { n: "2027", l: "Bungoma Governor bid" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yCard = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const yGlow = useTransform(scrollYProgress, [0, 1], [0, -140]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen hero-gradient overflow-hidden flex flex-col"
    >
      <motion.div
        style={{ y: yGlow }}
        aria-hidden="true"
        className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-orange-600/15 blur-[140px]"
      />
      <div
        aria-hidden="true"
        className="absolute bottom-0 -left-40 w-[480px] h-[480px] rounded-full bg-[#1E3E2B]/50 blur-[140px]"
      />

      <div className="relative max-w-7xl mx-auto w-full px-5 sm:px-8 pt-36 sm:pt-44 pb-16 grid lg:grid-cols-[1.25fr_1fr] gap-14 items-center flex-1">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="eyebrow mb-6"
            data-testid="hero-eyebrow"
          >
            Hon. Timothy Wanyonyi Wetangula — MP, Westlands
          </motion.p>

          <h1 className="font-display font-extrabold tracking-tight leading-[1.02] text-4xl sm:text-6xl lg:text-7xl">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{
                    duration: 0.95,
                    delay: 0.25 + i * 0.14,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  className={`block ${i === 2 ? "text-orange-500" : "text-stone-100"}`}
                  data-testid={`hero-headline-line-${i + 1}`}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
            className="mt-7 max-w-xl text-base sm:text-lg text-stone-400 leading-relaxed"
            data-testid="hero-subheadline"
          >
            Three-term Member of Parliament for Westlands County, elected on
            the Orange Democratic Movement ticket — now answering the call of home
            with a bid for Governor of Bungoma County in 2027.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-explore-vision-btn"
              onClick={() => scrollToSection("#vision")}
              className="rounded-full bg-orange-600 hover:bg-orange-500 px-7 py-3.5 mono text-xs font-semibold tracking-[0.15em] uppercase text-[#0B0C10] transition-colors duration-200"
            >
              Explore Vision 2027
            </button>
            <button
              data-testid="hero-become-supporter-btn"
              onClick={() => scrollToSection("#join")}
              className="rounded-full border border-white/20 hover:border-orange-500/60 hover:text-orange-400 px-7 py-3.5 mono text-xs font-semibold tracking-[0.15em] uppercase text-stone-200 transition-colors duration-200"
            >
              Become a Supporter
            </button>
          </motion.div>
        </div>

        <motion.div
          style={{ y: yCard }}
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="spotlight-frame max-w-md w-full mx-auto lg:ml-auto"
          data-testid="hero-portrait-frame"
        >
          <img
            src={PORTRAIT}
            alt="Hon. Tim Wanyonyi — statesman portrait"
            className="w-full h-[420px] sm:h-[520px] object-cover object-top max-h-[80vh]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C10]/90 via-transparent to-transparent" />
          <div className="absolute bottom-0 inset-x-0 p-6 flex items-end justify-between">
            <div>
              <p className="font-display font-bold text-lg leading-tight">Hon. Tim Wanyonyi</p>
              <p className="mono text-[11px] tracking-[0.2em] text-orange-400 uppercase mt-1 flex items-center gap-1.5">
                <MapPin size={12} /> Westlands · Nairobi
              </p>
            </div>
            <span className="mono text-[10px] tracking-[0.25em] text-stone-400 uppercase border border-white/15 rounded-full px-3 py-1.5 backdrop-blur-md bg-black/40">
              ODM
            </span>
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.2 }}
        className="relative border-t border-white/10"
        data-testid="hero-stats-ticker"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-2 lg:grid-cols-4">
          {STATS.map((s, i) => (
            <div
              key={s.l}
              className={`py-6 pr-6 ${i !== 0 ? "lg:border-l lg:border-white/10 lg:pl-6" : ""}`}
              data-testid={`hero-stat-${i + 1}`}
            >
              <p className="font-display font-extrabold text-2xl sm:text-3xl text-orange-500">{s.n}</p>
              <p className="mono text-[11px] tracking-[0.14em] uppercase text-stone-500 mt-1">{s.l}</p>
            </div>
          ))}
        </div>
        <button
          data-testid="hero-scroll-indicator"
          onClick={() => scrollToSection("#about")}
          aria-label="Scroll to story"
          className="hidden lg:flex absolute -top-16 right-8 w-11 h-11 rounded-full border border-white/15 items-center justify-center text-stone-400 hover:text-orange-400 hover:border-orange-500/50 transition-colors"
        >
          <ArrowDown size={16} className="animate-bounce" />
        </button>
      </motion.div>
    </section>
  );
};
